import { checkSession } from "@features/auth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Badge } from "@shadcn/ui/badge";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Container } from "lucide-react";
import { type PreloadedQuery, usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { z } from "zod";
import { formatConfigurationLabel, formatDimensions } from "~/components/lock/spaceConfiguration";
import { formatLockDateTime, formatLockDuration } from "~/components/lock/lockFormat";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import type { ended_reservationIdQuery } from "./__generated__/ended_reservationIdQuery.graphql";

export const query = graphql`
	query ended_reservationIdQuery($hubName: String!, $reservationId: UUID!) @relay_test_operation {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			street
			city
			postalCode
			hub @required(action: THROW) {
				id
			}
		}
		reservation(id: $reservationId) {
			... on LockReservation {
				id
				startedAt
				createdAt
				deactivatedAt
				contentNote
				note
				subtype {
					__typename
					... on B2ClockReservationSubtype {
						b2CLockSession {
							finalAmount
							amountCollected
							paymentStatus
						}
					}
				}
				space {
					id
					frame
					position
					configuration
					dimensions {
						widthMm
						heightMm
						depthMm
					}
					hub {
						id
					}
				}
			}
		}
		b2CLockSession(by: { reservationId: $reservationId }) {
			id
			startFee
			hourlyRate
			dailyCeiling
			weekCeiling
			finalAmount
			amountCollected
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/swap/ended/$reservationId")({
	beforeLoad: () => {
		document.title = "bacehub | Lock";
	},
	component: RouteComponent,
	validateSearch: zodValidator(z.object({ source: z.literal("b2c").optional() })),
	loader: async (ctx) => {
		const sessionExists = await checkSession();
		if (!sessionExists) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/swap/my${ctx.location.searchStr}` },
			});
		}
		return preloadQuery<ended_reservationIdQuery>(ctx.context.relayEnvironment, query, {
			hubName: ctx.params.hubMetaName,
			reservationId: ctx.params.reservationId,
		});
	},
});

function RouteComponent() {
	const { hubMetaName } = Route.useParams();
	const { source } = Route.useSearch();
	return (
		<EndedRentalPage queryRef={Route.useLoaderData()} hubMetaName={hubMetaName} service={source === "b2c" ? "LOCK_B2C" : "LOCK_B2B"} />
	);
}

export function EndedRentalPage(props: {
	queryRef: PreloadedQuery<ended_reservationIdQuery>;
	hubMetaName: string;
	service: "LOCK_B2B" | "LOCK_B2C";
}) {
	const { LL, locale } = useI18nContext();
	const i18n = LL["new-ui"];
	const { hubMetaName, service } = props;
	const backToLocks = service === "LOCK_B2C" ? "/$hubMetaName/lock" : "/$hubMetaName/swap/my";
	const { hubMeta, reservation, b2CLockSession } = usePreloadedQuery<ended_reservationIdQuery>(query, props.queryRef);

	// Reservation could not be resolved (e.g. wrong id): send the user back.
	if (!reservation?.id || !reservation.space) {
		return (
			<PageContainer
				footer={
					<Button size="lg" className="w-full" asChild>
						<Link to={backToLocks} params={{ hubMetaName }}>
							<T>{i18n.LOCK_ENDED_BACK_TO_LOCKS()}</T>
						</Link>
					</Button>
				}
			>
				<AppHeader service={service} />
				<div className="flex flex-col gap-2 px-6 pt-6">
					<h1 className="text-2xl font-breit font-medium">
						<T>{i18n.LOCK_ENDED_TITLE()}</T>
					</h1>
				</div>
			</PageContainer>
		);
	}

	const space = reservation.space;
	const startedAt = reservation.startedAt ?? reservation.createdAt;
	// `deactivatedAt` can briefly be null right after ending the rental (the
	// preloaded query may resolve before the backend reflects it). Fall back to
	// "now" so the Ended at / Duration rows are always shown with a value.
	const endedAt = reservation.deactivatedAt ?? new Date().toISOString();
	const content = reservation.contentNote ?? reservation.note ?? "";
	const subtype = reservation.subtype;
	const session = subtype?.__typename === "B2ClockReservationSubtype" ? subtype.b2CLockSession : null;
	const finalAmount = session ? (session.finalAmount ?? session.amountCollected) : null;
	const address = `${hubMeta.street}, ${hubMeta.postalCode} ${hubMeta.city}`;
	const hubLabel = formatHubName(hubMeta.name);

	const exploreServices = [
		{
			to: "/$hubMetaName/instant-buy" as const,
			image: "/consumer-home-assets/discover-take-away-card-1.webp",
			title: i18n.INSTANT_BUY_TITLE(),
			description: i18n.INSTANT_BUY_DESCRIPTION(),
		},
		{
			to: "/$hubMetaName/shop" as const,
			image: "/consumer-home-assets/discover-take-away-card-3.webp",
			title: i18n.SHOP_N_COLLECT_TITLE(),
			description: i18n.SHOP_N_COLLECT_DESCRIPTION(),
		},
	];

	return (
		<PageContainer
			footer={
				<Button size="lg" className="w-full shadow-xl" asChild>
					<Link to={backToLocks} params={{ hubMetaName }}>
						<T>{i18n.LOCK_ENDED_BACK_TO_LOCKS()}</T>
					</Link>
				</Button>
			}
		>
			<AppHeader service={service} />

			<div className="flex flex-col gap-6 px-6 pt-4">
				{/* Title + status badge */}
				<div className="flex items-center justify-between gap-2">
					<h1 className="text-2xl font-breit font-medium">
						<T>{i18n.LOCK_ENDED_TITLE()}</T>
					</h1>
					<Badge variant="destructive" className="shrink-0">
						<T>{i18n.LOCK_ENDED_BADGE()}</T>
					</Badge>
				</div>

				{/* Summary */}
				<div className="flex flex-col gap-2">
					<h2 className="text-xl font-breit font-semibold">
						<T>{i18n.LOCK_ENDED_SUMMARY()}</T>
					</h2>

					<div className="flex flex-col">
						<DetailRow label={i18n.LOCK_ENDED_RENTAL_ID()} value={reservation.id} />
						<DetailRow label={i18n.LOCK_ENDED_STARTED_AT()} value={formatLockDateTime(startedAt)} />
						<DetailRow label={i18n.LOCK_ENDED_ENDED_AT()} value={formatLockDateTime(endedAt)} />
						<DetailRow label={i18n.LOCK_ENDED_DURATION()} value={formatLockDuration(startedAt, endedAt, locale)} />
						{finalAmount != null ? (
							<DetailRow
								label={i18n.LOCK_CARD_PRICE()}
								value={new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR" }).format(finalAmount / 100)}
							/>
						) : null}
						<DetailRow
							label={i18n.LOCK_ENDED_COMPARTMENT()}
							value={`${formatConfigurationLabel(space.configuration)} (${formatDimensions(space.dimensions)})`}
						/>
						<DetailRow label={i18n.LOCK_ENDED_COMPARTMENT_NUMBER()} value={`${space.frame}-${space.position}`} />

						{content ? <DetailRow label={i18n.LOCK_ENDED_CONTENT()} value={content} /> : null}

						{/* Location */}
						<div className="flex flex-col gap-3 border-t pt-4">
							<span className="text-sm font-breit font-semibold text-foreground">
								<T>{i18n.LOCK_ENDED_LOCATION()}</T>
							</span>
							<div className="flex items-start gap-3">
								<Container className="size-6 shrink-0 text-foreground" />
								<div className="flex min-w-0 flex-1 flex-col gap-0.5">
									<p className="text-base font-breit font-medium text-foreground">{hubLabel}</p>
									<p className="text-sm text-muted-foreground underline break-words">{address}</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				{service === "LOCK_B2C" && b2CLockSession ? (
					<div className="-mx-6 flex flex-col gap-4 bg-zinc-100 px-6 py-4">
						<h2 className="text-xl font-breit font-semibold">{i18n.LOCK_ENDED_PRICE_BREAKDOWN()}</h2>
						<div className="flex flex-col gap-2 border-y py-3">
							<PriceRow label={i18n.LOCK_PRICE_HOURLY_RATE()} value={`${b2CLockSession.hourlyRate} EUR`} />
							<PriceRow label={i18n.LOCK_PRICE_DAILY_CAP()} value={`${b2CLockSession.dailyCeiling} EUR`} />
							<PriceRow label={i18n.LOCK_PRICE_WEEKLY_CAP()} value={`${b2CLockSession.weekCeiling} EUR`} />
							<PriceRow label={i18n.LOCK_PRICE_START_FEE()} value={`${b2CLockSession.startFee} EUR`} />
						</div>
						<PriceRow
							label={i18n.LOCK_PRICE_TOTAL()}
							value={`${((b2CLockSession.finalAmount ?? b2CLockSession.amountCollected) / 100).toFixed(2)} EUR`}
							emphasized
						/>
					</div>
				) : null}

				{/* Explore other services */}
				<div className="flex flex-col gap-4">
					<h2 className="text-2xl font-breit font-medium">
						<T>{i18n.LOCK_ENDED_EXPLORE_TITLE()}</T>
					</h2>
					<div className="grid grid-cols-2 gap-x-2 gap-y-4">
						{exploreServices.map((service) => (
							<Link key={service.to} to={service.to} params={{ hubMetaName }} className="block w-full space-y-3">
								<div className="aspect-[167/164] w-full overflow-hidden rounded-xl bg-muted shadow-sm">
									<img src={service.image} alt="" className="h-full w-full object-cover" />
								</div>
								<div>
									<p className="line-clamp-1 font-breit text-base font-medium leading-4 text-card-foreground">{service.title}</p>
									<p className="line-clamp-2 text-sm leading-5 text-muted-foreground">{service.description}</p>
								</div>
							</Link>
						))}
					</div>
				</div>
			</div>
		</PageContainer>
	);
}

/** Capitalises the hub name and appends "hub" (e.g. "buexe" -> "Buexe hub"). */
function formatHubName(name: string): string {
	const trimmed = name.trim();
	if (!trimmed) return "";
	const capitalised = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
	return /\bhub$/i.test(capitalised) ? capitalised : `${capitalised} hub`;
}

function DetailRow(props: { label: string; value: string }) {
	return (
		<div className="flex items-start gap-6 border-t py-6 first:border-t-0">
			<span className="w-1/3 shrink-0 text-sm font-breit font-semibold text-foreground">{props.label}</span>
			<span className="min-w-0 flex-1 text-base text-muted-foreground break-words">{props.value}</span>
		</div>
	);
}

function PriceRow(props: { label: string; value: string; emphasized?: boolean }) {
	return (
		<div className={`flex justify-between gap-4 text-sm ${props.emphasized ? "font-breit font-medium" : "text-muted-foreground"}`}>
			<span>{props.label}</span>
			<span>{props.value}</span>
		</div>
	);
}
